import appCss from '~/styles/app.css?url'

import {createRootRoute, HeadContent, Outlet, Scripts} from '@tanstack/react-router'
import {lazy} from 'react'

const TanStackRouterDevtools = lazy(() =>
    import('@tanstack/react-router-devtools').then((m) => ({default: m.TanStackRouterDevtools}))
)
const ReactQueryDevtools = lazy(() =>
    import('@tanstack/react-query-devtools').then((m) => ({default: m.ReactQueryDevtools}))
)
import {ErrorBoundary} from 'react-error-boundary'
import {QueryClient} from '@tanstack/react-query'
import {ReactNode, Suspense, useEffect, useState} from 'react'
import {v7 as uuid} from 'uuid'
import useLocaleParams from '~/hooks/useLocaleParams'
import useShopConfig from '~/hooks/useShopConfig'
import {shopQueryOptions} from '~/client/fn/shop'
import NotFound from '~/components/NotFound'
import Error from '~/components/Error'

export const Route = createRootRoute({
    head: () => ({
        meta: [
            {
                title: 'Gravity Shirts',
            },
            {
                charSet: 'utf-8',
            },
            {
                name: 'viewport',
                content: 'width=device-width, initial-scale=1',
            },
            {
                name: 'theme-color',
                content: '#101015',
            },
            {
                name: 'color-scheme',
                content: 'dark',
            },
        ],
        links: [
            {
                rel: 'apple-touch-icon',
                sizes: '180x180',
                href: '/favicon/apple-touch-icon.png',
            },
            {
                rel: 'icon',
                type: 'image/png',
                sizes: '32x32',
                href: '/favicon/favicon-32x32.png',
            },
            {
                rel: 'icon',
                type: 'image/png',
                sizes: '16x16',
                href: '/favicon/favicon-16x16.png',
            },
            {
                rel: 'manifest',
                href: '/favicon/site.webmanifest',
            },
            {
                rel: 'preconnect',
                href: 'https://image.spreadshirtmedia.net',
            },
            {
                rel: 'dns-prefetch',
                href: 'https://image.spreadshirtmedia.net',
            },
            {
                rel: 'preconnect',
                href: 'https://cdn.sanity.io',
            },
            {
                rel: 'dns-prefetch',
                href: 'https://cdn.sanity.io',
            },
            {
                rel: 'stylesheet',
                href: appCss,
            },
            {
                rel: 'preload',
                href: appCss,
                as: 'style',
            },
        ],
    }),
    component: RootComponent,
    notFoundComponent: NotFoundComponent,
    errorComponent: ErrorComponent,
    headers: ({loaderData}) => {
        // @ts-ignore
        const {isDevelopment, isBot} = loaderData
        return {
            ...(!isBot
                ? {
                      'Cache-Control': 's-maxage=3600, stale-while-revalidate=3600',
                  }
                : {}),
            ...(!isDevelopment
                ? {
                      'Content-Security-Policy': `default-src 'self'; style-src 'self' data: 'unsafe-inline'; script-src 'self' 'unsafe-inline'; img-src 'self' data: https://image.spreadshirtmedia.net https://cdn.sanity.io/images/; frame-ancestors 'self'; frame-src 'self'`,
                  }
                : {}),
        }
    },
    context: ({context}) => {
        return {
            ...context,
            // @ts-ignore
            queryClient: context.queryClient as QueryClient,
        }
    },
    loader: async ({context}) => {
        const nonce = uuid()
        const shop = await context.queryClient.ensureQueryData(shopQueryOptions())
        return {...shop, nonce}
    },
})

function RootComponent() {
    return (
        <RootDocument>
            <Outlet />
        </RootDocument>
    )
}

function NotFoundComponent() {
    return (
        <RootDocument>
            <NotFound />
        </RootDocument>
    )
}

function ErrorComponent() {
    return (
        <RootDocument>
            <Error />
        </RootDocument>
    )
}

function RootDocument({children}: {children: ReactNode}) {
    const shopConfig = useShopConfig()
    const {lang} = useLocaleParams()
    // noinspection HtmlRequiredTitleElement
    return (
        <html lang={lang ?? 'en'}>
            <head>
                <HeadContent />
            </head>
            <body>
                <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:p-4 focus:bg-midnight focus:text-white">
                    Skip to main content
                </a>
                <Suspense fallback={null}>
                    <ErrorBoundary fallback={<Error />}>{children}</ErrorBoundary>
                </Suspense>
                {shopConfig.isDevelopment && <DevTools />}
                <Scripts />
            </body>
        </html>
    )
}

function DevTools() {
    const [mounted, setMounted] = useState(false)
    useEffect(() => setMounted(true), [])
    if (!mounted) return null
    return (
        <Suspense fallback={null}>
            <TanStackRouterDevtools position="bottom-right" />
            <ReactQueryDevtools buttonPosition="bottom-left" />
        </Suspense>
    )
}
